/**
 * Auth store — Zustand global state for authentication.
 *
 * WHY memory storage for access token:
 * Storing the JWT in memory (not localStorage) prevents XSS attacks from
 * exfiltrating tokens. The refresh token is in an HttpOnly cookie so it's
 * not accessible to JavaScript at all.
 *
 * On page refresh: the access token is lost, but the refresh cookie persists.
 * The app calls POST /api/v1/auth/refresh on startup to get a new access token.
 *
 * DEPENDS ON: zustand
 */
import { create } from "zustand";
import type { UserRole } from "@bosh-bordon/shared";

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  workspace_id: string;
}

interface AuthState {
  // Access token stored in memory only — never persisted to localStorage
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setAuth: (token: string, user: AuthUser) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start true — app checks session on mount

  setAuth: (token, user) =>
    set({ accessToken: token, user, isAuthenticated: true, isLoading: false }),

  clearAuth: () =>
    set({ accessToken: null, user: null, isAuthenticated: false, isLoading: false }),

  setLoading: (loading) => set({ isLoading: loading }),
}));
